<script setup lang="ts">
import { useBillingUtils } from '@/use/billingUtils';
import { useToast } from '@/use/toast';
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import { useBillingStore } from '@/stores/billing';
import { Badge, Button, LayoutGap, Prose } from '@dabble/quillui';
import { computed, onMounted, ref, watch } from 'vue';
import AppSkeleton from '@/components/AppSkeleton/AppSkeleton.vue';

interface InvoiceLineItem {
  id: string;
  description?: string | null;
}
interface Invoice {
  id: string;
  created: number;
  total: number;
  currency: string;
  status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void' | null;
  hosted_invoice_url?: string | null;
  lines: { data: InvoiceLineItem[] };
  auto_advance?: boolean | null;
  payment_processing?: boolean;
}

const billingStore = useBillingStore();
const { formatPrice, formatUnixDate } = useBillingUtils();
const { t } = useI18n();
const { showToast } = useToast();

const localInvoices = ref<Invoice[]>([]);
const loadingMore = ref(false);
const noMoreInvoices = ref(false);
const retrying = ref<Record<string, boolean>>({});
const initialLoadDone = ref(false);

onMounted(() => {
  if (!billingStore.invoices) return;
  localInvoices.value = billingStore.invoices as unknown as Invoice[];
  noMoreInvoices.value = localInvoices.value.length < 3;
  initialLoadDone.value = true;
});

// Keep localInvoices in sync with the store after refreshes (e.g. retry, pause-with-void).
// Patches matching entries by id and prepends any brand-new invoices from the store.
watch(
  () => billingStore.invoices,
  (storeInvoices) => {
    if (!storeInvoices) return;
    const store = storeInvoices as unknown as Invoice[];
    const storeById = new Map(store.map((i) => [i.id, i]));
    const localIds = new Set(localInvoices.value.map((i) => i.id));
    const patched = localInvoices.value.map((i) => storeById.get(i.id) ?? i);
    const newOnes = store.filter((i) => !localIds.has(i.id));
    localInvoices.value = newOnes.length ? [...newOnes, ...patched] : patched;
  },
);

const invoices = computed(() => localInvoices.value);
const isMobile = ref(false);

const statusColors: Record<string, 'info' | 'success' | 'danger' | 'warning' | 'neutral'> = {
  void: 'info',
  uncollectible: 'info',
  draft: 'info',
  paid: 'success',
  open: 'danger',
  processing: 'warning',
};

async function loadMoreInvoices() {
  if (loadingMore.value || noMoreInvoices.value || localInvoices.value.length === 0) return;

  loadingMore.value = true;
  const lastInvoiceId = localInvoices.value[localInvoices.value.length - 1]?.id;
  if (!lastInvoiceId) {
    loadingMore.value = false;
    return;
  }

  try {
    const fetched = (await billingStore.fetchInvoices(lastInvoiceId)) as unknown as Invoice[];
    if (fetched.length < 5) noMoreInvoices.value = true;
    const seen = new Set(localInvoices.value.map((i) => i.id));
    const newOnes = fetched.filter((i) => !seen.has(i.id));
    if (newOnes.length) {
      localInvoices.value = [...localInvoices.value, ...newOnes];
    } else {
      noMoreInvoices.value = true;
    }
  } catch (err) {
    logError('Failed to load more invoices:', err);
    showToast('danger', t('billing_error_loading_invoices'));
  } finally {
    loadingMore.value = false;
  }
}

async function retryPayment(invoice: Invoice) {
  retrying.value = { ...retrying.value, [invoice.id]: true };
  try {
    const updated = (await billingStore.retryInvoicePayment(invoice.id)) as unknown as Invoice | null;
    if (updated) {
      localInvoices.value = localInvoices.value.map((i) => (i.id === updated.id ? updated : i));
    }
  } catch (err) {
    logError('Failed to retry invoice payment:', err);
    showToast('danger', t('billing_error_retry_payment'));
  } finally {
    const newRetrying = { ...retrying.value };
    delete newRetrying[invoice.id];
    retrying.value = newRetrying;
  }
}

function getStatus(invoice: Invoice): keyof typeof statusColors {
  if (!invoice.auto_advance && invoice.status === 'open') return 'void';
  // Open, but a bank debit is already clearing it (DAB-1690) — nothing to retry.
  if (invoice.status === 'open' && invoice.payment_processing) return 'processing';
  return invoice.status || 'void';
}

function formatInvoiceDate(timestamp: number): string {
  return formatUnixDate(timestamp, isMobile.value ? 'P' : 'PP');
}

function getInvoiceDescription(invoice: Invoice): string {
  const description = invoice.lines?.data?.[invoice.lines.data.length - 1]?.description;
  return description?.replace(/^\d+ × | \(at .[\d.]+ \/ \w+\)$/g, '') || '';
}

function openInvoice(url: string | null | undefined) {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
}
</script>

<template>
  <LayoutGap direction="column" size="none" fill class="billing-invoice-list">
    <div :class="['invoices', { small: isMobile }]">
      <template v-if="initialLoadDone && invoices.length === 0">
        <div class="invoices-empty">
          {{ t('billing_invoice_none') }}
        </div>
      </template>

      <template v-else>
        <div v-for="invoice in invoices" :key="invoice.id" class="invoice-row">
          <div class="invoice-cell invoice-created">
            <Button
              variant="primary"
              finish="link"
              icon-right="open-in-new"
              :disabled="!invoice.hosted_invoice_url"
              @click="openInvoice(invoice.hosted_invoice_url)"
            >
              {{ formatInvoiceDate(invoice.created) }}
            </Button>
          </div>
          <Prose variant="secondary" size="small" class="invoice-description">
            {{ getInvoiceDescription(invoice) }}
          </Prose>
          <div class="invoice-cell invoice-amount">
            <Prose>{{ formatPrice(invoice.total, invoice.currency, true) }}</Prose>
          </div>
          <div class="invoice-cell invoice-status">
            <LayoutGap direction="row" vertical-align="center" size="small">
              <Button
                v-if="getStatus(invoice) === 'open'"
                variant="secondary"
                finish="outlined"
                size="small"
                icon-left="billing"
                :disabled="!!retrying[invoice.id]"
                @click="retryPayment(invoice)"
              >
                {{ t('billing_invoice_retry') }}
              </Button>
              <Badge :variant="statusColors[getStatus(invoice)]">
                {{ t(`billing_invoice_${getStatus(invoice)}`) }}
              </Badge>
            </LayoutGap>
          </div>
        </div>

        <div v-if="loadingMore" class="invoice-row invoice-row--loading" aria-hidden="true">
          <div class="invoice-cell"><AppSkeleton width="7rem" height="1.1rem" /></div>
          <div class="invoice-cell"><AppSkeleton width="4.5rem" height="1.1rem" /></div>
          <div class="invoice-cell"><AppSkeleton width="5.5rem" height="1.1rem" /></div>
        </div>

        <div v-else-if="!noMoreInvoices" class="load-more">
          <Button variant="primary" finish="link" @click="loadMoreInvoices">
            {{ t('billing_invoice_load_more') }}
          </Button>
        </div>
      </template>
    </div>
  </LayoutGap>
</template>

<style lang="scss" scoped>
// The approved mockup's invoice table: a bordered list, one row per receipt —
// date link | description | amount | status.
.invoices {
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 1px solid theme('color-border-primary');
  border-radius: borderRadius('medium');
  overflow: hidden;
}

.invoice-row {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr) auto auto;
  gap: space('small');
  align-items: center;
  width: 100%;
  padding: space('xsmall') space('small');
  box-sizing: border-box;
}

.invoice-row + .invoice-row {
  border-top: 1px solid theme('color-border-primary');
}

.invoice-description {
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.invoice-amount :deep(p) {
  margin: 0;
  font-weight: fontWeight('semibold');
  font-variant-numeric: tabular-nums;
}

.invoices-empty {
  padding: space('small');
  color: theme('color-text-secondary');
}

.load-more {
  display: flex;
  justify-content: center;
  padding: space('small') 0;
  border-top: 1px solid theme('color-border-primary');
}

@media (max-width: 640px) {
  .invoice-row {
    grid-template-columns: max-content auto auto;
  }

  .invoice-description {
    grid-column: 1 / -1;
    grid-row: 2;
    white-space: normal;
  }
}
</style>
